import type { FundCalendarInvestor, FundCalendarMeeting, FundCalendarMember } from '../api/fund-calendar';

/** Monday 00:00 local time of the week containing `date`. */
export function weekStart(date: Date): Date {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  day.setDate(day.getDate() - ((day.getDay() + 6) % 7));
  return day;
}

/** Calendar days, not 24-hour steps, so a DST change never shifts midnight. */
export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export const monthStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
export const addMonths = (date: Date, months: number) => new Date(date.getFullYear(), date.getMonth() + months, 1);

/** Whole Monday-to-Sunday weeks covering the month: 28 to 42 days. */
export function monthGrid(month: Date): Date[] {
  const first = weekStart(monthStart(month));
  const last = weekStart(new Date(month.getFullYear(), month.getMonth() + 1, 0));
  const days: Date[] = [];
  for (let day = first; day <= last || days.length % 7; day = addDays(day, 1)) days.push(day);
  return days;
}

export const isPast = (meeting: { end: string }, now: Date) => Date.parse(meeting.end) <= now.getTime();

// Owner hues are non-semantic: never green/amber/red (status), coral (action) or blue (info).
const OWNER_COLORS = ['#8f7fd6', '#b089b8', '#7f8fb0', '#a89f91', '#9c7f9f', '#8a98a3'];

/** The viewer always takes the first hue, so "You" looks the same every week. */
export function ownerColors(members: FundCalendarMember[], viewer: string): Record<string, string> {
  const ordered = members.map(member => member.email)
    .sort((a, b) => Number(a !== viewer) - Number(b !== viewer) || a.localeCompare(b));
  return Object.fromEntries(ordered.map((email, i) => [email, OWNER_COLORS[i % OWNER_COLORS.length]]));
}

export function initials(email: string, members: FundCalendarMember[]): string {
  const name = members.find(member => member.email === email)?.name?.trim() || email.split('@')[0];
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : name.slice(0, 2);
  return letters.toUpperCase();
}

type NamedLp = Pick<FundCalendarInvestor, 'display_name' | 'institution_name'>;
export const lpName = (person: NamedLp) => person.institution_name || person.display_name;
export const meetingName = (meeting: { summary: string; investors: NamedLp[] }) =>
  [...new Set(meeting.investors.map(lpName))].join(' + ') || meeting.summary;
const lpKey = (person: FundCalendarInvestor) =>
  person.institution_id ? `institution:${person.institution_id}` : `contact:${person.counterparty_id}`;

/** Side-by-side lanes for meetings whose times overlap within one day column. */
export function layoutDay<T extends { start: string; end: string }>(items: T[]) {
  const sorted = [...items].sort((a, b) =>
    Date.parse(a.start) - Date.parse(b.start) || Date.parse(b.end) - Date.parse(a.end));
  const placed: { item: T; lane: number; lanes: number }[] = [];
  let cluster: typeof placed = [];
  let laneEnds: number[] = [];
  let clusterEnd = -Infinity;
  const close = () => { cluster.forEach(entry => entry.lanes = laneEnds.length); cluster = []; laneEnds = []; };
  for (const item of sorted) {
    const start = Date.parse(item.start), end = Date.parse(item.end);
    if (cluster.length && start >= clusterEnd) close();
    let lane = laneEnds.findIndex(laneEnd => laneEnd <= start);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(end); } else laneEnds[lane] = end;
    const entry = { item, lane, lanes: 1 };
    cluster.push(entry);
    placed.push(entry);
    clusterEnd = Math.max(clusterEnd, end);
  }
  close();
  return placed;
}

/** First and last whole hour to draw: the working day, widened to fit every meeting. */
export function hourRange(meetings: FundCalendarMeeting[]): [number, number] {
  let first = 8, last = 18;
  for (const meeting of meetings) {
    const start = new Date(meeting.start), end = new Date(meeting.end);
    first = Math.min(first, start.getHours());
    last = Math.max(last, sameDay(start, end) ? end.getHours() + (end.getMinutes() ? 1 : 0) : 24);
  }
  return [first, Math.min(last, 24)];
}

/** Upcoming meetings with one LP that different teammates lead this week. */
export function overlapGroups(meetings: FundCalendarMeeting[], now: Date): FundCalendarMeeting[][] {
  const byLp = new Map<string, FundCalendarMeeting[]>();
  for (const meeting of meetings) {
    if (isPast(meeting, now)) continue;
    for (const key of new Set(meeting.investors.map(lpKey))) byLp.set(key, [...(byLp.get(key) || []), meeting]);
  }
  return [...byLp.values()].filter(group => group.length > 1 && new Set(group.map(m => m.lead)).size > 1);
}

const anyAccepted = (meeting: FundCalendarMeeting) => meeting.investors.some(person => person.response === 'accepted');
const allDeclined = (meeting: FundCalendarMeeting) =>
  meeting.investors.length > 0 && meeting.investors.every(person => person.response === 'declined');

export type MeetingStatus = { word: string; tone: '' | 'amber' | 'red'; spark: boolean };

/** One status per meeting; every coloured state carries its word (DESIGN-SYSTEM §1). */
export function meetingStatus(meeting: FundCalendarMeeting, now: Date, overlapIds: Set<string>): MeetingStatus {
  if (isPast(meeting, now))
    return meeting.notes.length ? { word: 'Notes logged', tone: '', spark: false }
      : { word: '✎ No notes', tone: 'amber', spark: false };
  if (allDeclined(meeting)) return { word: '✕ Declined', tone: 'red', spark: false };
  if (overlapIds.has(meeting.id)) return { word: '⚠ Overlap', tone: 'amber', spark: false };
  if (!anyAccepted(meeting)) {
    const tentative = meeting.investors.some(person => person.response === 'tentative');
    return { word: tentative ? '◌ Tentative' : '◌ Unconfirmed', tone: 'amber', spark: false };
  }
  if (meeting.investors.some(person => person.call_brief || person.call_brief_status === 'ready'))
    return { word: 'Brief ready', tone: '', spark: true };
  if (meeting.investors.some(person => person.call_brief_status === 'generating'))
    return { word: 'Brief in progress', tone: '', spark: false };
  return { word: 'No brief yet', tone: '', spark: false };
}

export type CoordinationRow =
  | { kind: 'overlap'; meeting: FundCalendarMeeting; group: FundCalendarMeeting[] }
  | { kind: 'declined' | 'unconfirmed' | 'nonotes'; meeting: FundCalendarMeeting };

/** Coordination signals only: overlaps, LP replies, and past meetings without notes. */
/** One overlap's identity: its set of meetings, so two groups that start alike stay distinct. */
export function overlapKey(group: FundCalendarMeeting[]): string {
  return group.map(meeting => meeting.id).sort().join('+');
}

export function coordination(meetings: FundCalendarMeeting[], now: Date,
                             groups: FundCalendarMeeting[][]): CoordinationRow[] {
  // Several LPs on the same calls are one overlap; repeating it would also repeat its {#each} key.
  const seen = new Set<string>();
  const rows: CoordinationRow[] = [];
  for (const group of groups) {
    const key = overlapKey(group);
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({ kind: 'overlap', meeting: group[0], group });
  }
  for (const meeting of meetings) {
    if (isPast(meeting, now)) {
      if (!meeting.notes.length) rows.push({ kind: 'nonotes', meeting });
    } else if (allDeclined(meeting)) rows.push({ kind: 'declined', meeting });
    else if (!anyAccepted(meeting)) rows.push({ kind: 'unconfirmed', meeting });
  }
  const order = { overlap: 0, declined: 1, unconfirmed: 2, nonotes: 3 };
  return rows.sort((a, b) => order[a.kind] - order[b.kind] || Date.parse(a.meeting.start) - Date.parse(b.meeting.start));
}
